import {Component, HostListener, OnInit} from '@angular/core';
import {Tenant} from "../../model/tenant";
import {Template} from "../../model/template";
import {TemplateFilters} from "../../model/filters/template-filters";
import {FiltersService} from "../../services/filters.service";
import {TenantsService} from "../../services/tenants.service";
import {CommonMessagesService} from "../../services/common-messages.service";
import {TemplatesService} from "../../services/templates.service";
import {AuthGuard} from "../../common/auth-guard.service";
import {SelectItem} from "primeng/api";
import {DeviceCategoriesService} from "../../services/device-categories.service";
import {TranslocoService} from "@ngneat/transloco";
import * as moment from "moment";
import {TimezoneService} from "../../services/timezone.service";
import {SortOrder} from "../../model/sort-order";

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss']
})
export class TemplatesComponent implements OnInit {

  public templates: Template[];
  public totalTemplates: number;
  public loadingTemplates: boolean;

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: TemplateFilters;
  public filtersFilled: boolean;
  public creationRangeDates: Date[];
  public updateRangeDates: Date[];

  public tenants: Tenant[];
  public deviceCategories: SelectItem[];
  public types: SelectItem[];

  constructor(private templatesService: TemplatesService,
              private tenantsService: TenantsService,
              private deviceCategoriesService: DeviceCategoriesService,
              private commonMessagesService: CommonMessagesService,
              public authGuard: AuthGuard,
              private i18n: TranslocoService,
              private timezoneService: TimezoneService,
              private filtersService: FiltersService<TemplateFilters>) {
    this.currentFilters = new TemplateFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.loadFilters().then(() => {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      if (this.currentFilters?.hasOwnProperty('creationRangeDates')) {
        this.creationRangeDates = [new Date(this.currentFilters.creationRangeDates[0]), new Date(this.currentFilters.creationRangeDates[1])];
      }
      if (this.currentFilters?.hasOwnProperty('updateRangeDates')) {
        this.updateRangeDates = [new Date(this.currentFilters.updateRangeDates[0]), new Date(this.currentFilters.updateRangeDates[1])];
      }
    });
    this.templates = new Array(this.rows);
  }

  private loadTemplates(offset: number, limit: number): void {
    this.loadingTemplates = true;
    this.templates = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.formatterDateForm(this.creationRangeDates, 'creationRangeDates');
    this.formatterDateForm(this.updateRangeDates, 'updateRangeDates');
    this.templatesService.getTemplates(offset, limit, this.currentFilters)
      .subscribe(response => {
        this.templates = response.templates;
        this.totalTemplates = response.totalElements;
        this.loadingTemplates = false;
      });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadTemplates(event.first / event.rows, event.rows);
  }

  private loadFilters(): Promise<any> {
    this.loadTypes();
    let promises: Array<Promise<any>> = [this.deviceCategoriesService.getDeviceCategories().toPromise()];
    if (this.authGuard.superAdminRequired()) {
      promises.push(this.tenantsService.getTenants().toPromise());
    }
    return Promise.all(promises).then(response => {
      this.deviceCategories = response[0].deviceCategories
        .map(category => category.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(categoryName => {
          return {
            label: categoryName,
            value: categoryName
          };
        });
      if (this.authGuard.superAdminRequired()) {
        this.tenants = response[1].tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  private loadTypes(): void {
    this.i18n.selectTranslate('model.device').subscribe(() => {
      this.types = [
        {label: this.i18n.translate('model.device'), value: 'DEVICE'},
        {label: this.i18n.translate('model.asset'), value: 'ASSET'}
      ];
    });
  }

  resetFilters(): void {
    this.currentFilters = new TemplateFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.creationRangeDates = null;
    this.updateRangeDates = null;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadTemplates(this.first / this.rows, this.rows);
  }

  confirmAndDelete(template: Template): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteTemplate(template.uuid, template.tenant.uuid);
    });
  }

  deleteTemplate(templateUuid: string, tenantUuid: string): void {
    this.templatesService.deleteTemplate(templateUuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadTemplates(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const uuids = this.selected.map(item => item.uuid);

    this.templatesService.deleteTemplates(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadTemplates(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  trimFieldsForm(filters: TemplateFilters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  formatterDateForm(rangeDates: Date[], filtersField: string): void {
    if (rangeDates && rangeDates.length > 1) {
      if (!rangeDates[1]) {
        rangeDates[1] = moment.utc().toDate();
      }
      const utcRange = this.timezoneService.timestampRangeFromCalendarData(rangeDates);
      if (filtersField === 'creationRangeDates') {
        this.currentFilters.creationRangeDates = [utcRange.from, utcRange.until];
      } else {
        this.currentFilters.updateRangeDates = [utcRange.from, utcRange.until];
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
